import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, LoginCredentials, AuthUser } from '../../types/auth';
import { authApiService } from '../../services/authApiService';

interface ExtendedAuthState extends AuthState {
  users: AuthUser[];
}

const initialState: ExtendedAuthState = {
  user: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  isCheckingSession: true,
  users: []
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials) => {
    return await authApiService.login(credentials);
  }
);

export const checkSession = createAsyncThunk(
  'auth/checkSession',
  async () => {
    return await authApiService.checkSession();
  }
);

export const logoutAsync = createAsyncThunk(
  'auth/logout',
  async () => {
    await authApiService.logout();
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data: { name: string }) => {
    return await authApiService.updateProfile(data);
  }
);

export const fetchUsers = createAsyncThunk(
  'auth/fetchUsers',
  async () => {
    return await authApiService.fetchUsers();
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Login failed';
      })
      // Check session
      .addCase(checkSession.pending, (state) => {
        state.isCheckingSession = true;
        state.error = null;
      })
      .addCase(checkSession.fulfilled, (state, action) => {
        state.isCheckingSession = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(checkSession.rejected, (state) => {
        state.isCheckingSession = false;
        state.user = null;
        state.isAuthenticated = false;
      })
      // Logout
      .addCase(logoutAsync.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Profile update failed';
      })
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<AuthUser[]>) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch users';
      });
  }
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
